import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useToast } from '../ui/Alert';
import { usersApi } from '../../api/endpoints/users';
import { usernameFromEmail } from '../../lib/username';
import { ApiError } from '../../types/api';

interface QuickAddEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: string;
}

/**
 * Lightweight invite flow scoped to a single permission: `paylinks.add`. Lives
 * inside the QuickPaylinkModal so a merchant can delegate paylink creation to
 * an employee in one click without leaving the create-paylink screen.
 *
 * Mirrors the user-not-found → create-account pattern from /users/UserList.tsx
 * so it matches what users see in the full team page.
 */
export function QuickAddEmployeeModal({ isOpen, onClose, storeId }: QuickAddEmployeeModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  // Backend flips this to true when the email doesn't have an account yet -
  // we then collect a username and call /users with createAccount: { username }.
  const [needsAccount, setNeedsAccount] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');

  const reset = () => {
    setEmail('');
    setUsername('');
    setNeedsAccount(false);
    setUsernameError('');
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const inviteMutation = useMutation({
    mutationFn: () =>
      usersApi.add(storeId, {
        email: email.trim(),
        // Friendly role label; the actual access is determined by `permissions`.
        newRoleName: 'paylink_creator',
        // Single narrow permission: enough to create paylinks (incl. /paylinks/quick
        // via API key) but nothing else. Merchant can broaden this later from the
        // full team page.
        permissions: ['paylinks.add'],
        ...(needsAccount ? { createAccount: { username: username.trim() } } : {}),
      }),
    // Backend returns 200 for every outcome and signals the result via `status`
    // (mirrors the legacy /stores/users/add contract). A resolved request does
    // NOT mean the invite succeeded - we must branch on the status here.
    onSuccess: (data) => {
      const status = data?.status;

      if (status === 'ok') {
        queryClient.invalidateQueries({ queryKey: ['users', storeId] });
        toast(
          'success',
          t('paylinks.employeeInvited', 'Employee invited - they can now create paylinks.'),
        );
        handleClose();
        return;
      }

      if (status === 'user not found') {
        // No account for this email yet -> collect a username so we can create
        // one. 200, not 404, so this never reaches onError. Prefill it from the
        // email's local part (xyasserksa@gmail.com -> "xyasserksa"); editable.
        if (!needsAccount) {
          setNeedsAccount(true);
          setUsername(usernameFromEmail(email));
          setUsernameError('');
        }
        return;
      }

      if (status === 'username taken') {
        setUsernameError(t('users.usernameTaken', 'This username is taken, please choose another.'));
        return;
      }

      if (status === 'user already exists') {
        toast('error', t('users.alreadyExists', 'This user is already a team member.'));
        return;
      }

      toast('error', t('common.error'));
    },
    onError: (error) => {
      // Defensive fallback if the backend ever switches to a real 404.
      const isUserNotFound =
        (error instanceof ApiError &&
          (error.code === 'USER_NOT_FOUND' ||
            error.status === 404 ||
            /user.*not\s*found/i.test(error.message))) ||
        (error instanceof Error && /user.*not\s*found/i.test(error.message));

      if (isUserNotFound && !needsAccount) {
        setNeedsAccount(true);
        setUsername('');
      } else {
        toast('error', t('common.error'));
      }
    },
  });

  const goToFullTeamPage = () => {
    handleClose();
    navigate(`/${storeId}/users`);
  };

  const isDisabled =
    !email.trim() || (needsAccount && !username.trim()) || inviteMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        needsAccount
          ? t('paylinks.createEmployeeAccount', 'Create employee account')
          : t('paylinks.addEmployeeForPaylinks', 'Invite a paylink creator')
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => inviteMutation.mutate()}
            isLoading={inviteMutation.isPending}
            disabled={isDisabled}
          >
            {needsAccount
              ? t('paylinks.createAndInvite', 'Create & invite')
              : t('paylinks.invite', 'Invite')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Scope explainer */}
        <div className="flex items-start gap-2.5 rounded-lg border border-accent/30 bg-accent/5 px-3 py-2.5">
          <ShieldCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />
          <div className="text-xs text-text-secondary leading-relaxed">
            {t(
              'paylinks.employeeScopeHint',
              'The invited employee will only be able to create paylinks (paylinks.add). They will not see orders, customers, or settings unless you grant more permissions later from the team page.',
            )}
          </div>
        </div>

        <Input
          label={t('common.email')}
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // If the user changes the email, drop back to the simple flow - the
            // new email may belong to an existing account.
            if (needsAccount) {
              setNeedsAccount(false);
              setUsername('');
              setUsernameError('');
            }
          }}
          placeholder="employee@example.com"
        />

        {needsAccount && (
          <>
            <div className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2.5">
              <p className="text-xs text-text-secondary leading-relaxed">
                {t(
                  'users.noAccountFound',
                  'No account found with this email. Fill in a username to create a new account and add them to your team.',
                )}
              </p>
            </div>
            <Input
              label={t('users.username', 'Username')}
              required
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (usernameError) setUsernameError('');
              }}
              error={usernameError || undefined}
              placeholder={t('users.usernamePlaceholder', 'Enter a username')}
            />
          </>
        )}

        <button
          type="button"
          onClick={goToFullTeamPage}
          className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
        >
          {t('paylinks.manageAllPermissions', 'Manage all permissions')}
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>
    </Modal>
  );
}
